import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, AlertTriangle, Coins, TrendingUp, Users, ShieldCheck, GraduationCap, MapPin, Milestone, BadgeCheck, FileSignature, Landmark, Calculator, FileText } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getCmsLayout, CmsLayout } from "@/data/cmsStore";

export default function CandidacyInfoPage() {
  const { t, isRtl, lang } = useI18n();
  const c = t.candidacy;
  const [activeTab, setActiveTab] = useState("general");
  const [layout, setLayout] = useState<CmsLayout>([]);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#jeunes') {
      setActiveTab('youth');
      setTimeout(() => document.getElementById("parcours")?.scrollIntoView({ behavior: "smooth" }), 200);
    } else if (hash === '#general') {
      setActiveTab('general');
      setTimeout(() => document.getElementById("parcours")?.scrollIntoView({ behavior: "smooth" }), 200);
    }

    // Load CMS Layout
    setLayout(getCmsLayout("candidature"));
    
    const handleLayoutChange = (e: any) => {
      if (e.detail?.page === "candidature") {
        setLayout(getCmsLayout("candidature"));
      }
    };
    window.addEventListener("cmsLayoutChanged", handleLayoutChange);
    return () => window.removeEventListener("cmsLayoutChanged", handleLayoutChange);
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const scrollToTabs = (tab: string) => {
    setActiveTab(tab);
    setTimeout(() => {
      document.getElementById("parcours")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-background" dir={isRtl ? 'rtl' : 'ltr'}>
      <Navbar />

      {layout.length > 0 ? (
        layout.map((section) => {
          if (!section.visible) return null;
          
          switch (section.type) {
            case "CandidacyHeroSection":
              return (
                <section key={section.id} className="relative pt-32 pb-20 overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 via-slate-900 to-accent/60 opacity-80" />
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1540910419892-4a36d2c3266c?q=80&w=2070&auto=format&fit=crop')] bg-cover bg-center mix-blend-overlay opacity-20" />
        
        <div className="container relative z-10 text-center px-4">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}>
            <h1 className="text-4xl md:text-6xl font-display font-extrabold mb-6 leading-tight">
              {c.title} <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-amber-300">{c.titleHighlight}</span>
            </h1>
            <p className="text-lg md:text-2xl text-slate-300 font-body max-w-3xl mx-auto mb-10">
              {c.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
              <Button size="lg" className="text-lg md:text-xl font-bold px-8 py-7 bg-primary text-white hover:bg-primary/90 shadow-xl rounded-2xl border border-white/20 transition-all hover:scale-105" onClick={() => scrollToTabs('general')}>
                {c.tabGeneral} <ArrowRight className={`w-5 h-5 ${isRtl ? 'mr-2 rotate-180' : 'ml-2'} opacity-80`} />
              </Button>
              <Button size="lg" className="text-lg md:text-xl font-bold px-8 py-7 bg-accent text-accent-foreground hover:bg-accent/90 shadow-xl rounded-2xl border border-white/20 transition-all hover:scale-105" onClick={() => scrollToTabs('youth')}>
                {c.tabYouth} <TrendingUp className={`w-5 h-5 ${isRtl ? 'mr-2' : 'ml-2'} opacity-80`} />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
            );

            case "CandidacyTabsSection":
              return (
                <div key={section.id} className="container py-12 max-w-6xl mx-auto" id="parcours">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex justify-center mb-12">
            <Button 
               variant="outline" 
               className="rounded-full px-8 py-6 font-bold text-lg shadow-sm border border-border/50 bg-card hover:bg-muted text-foreground transition-all group"
               onClick={() => document.getElementById("timeline-section")?.scrollIntoView({ behavior: "smooth" })}
            >
              {c.btnParcours || "Parcours de candidature"}
              <ArrowRight className={`ml-3 w-5 h-5 transition-transform group-hover:translate-y-1 ${isRtl ? '-rotate-[270deg]' : 'rotate-90'}`} />
            </Button>
          </div>

          <TabsContent value="general" className="mt-0 outline-none space-y-20">
            <section className="bg-card border border-border/40 rounded-[2.5rem] p-8 md:p-12 shadow-sm">
              <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
                <h2 className="text-3xl md:text-5xl font-display font-bold text-foreground mb-4">{c.eligibilityTitle}</h2>
              </motion.div>

              <motion.div className="grid md:grid-cols-3 gap-8" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                {[
                  { icon: BadgeCheck, num: "🇲🇦", title: c.eligibilityItems?.[0]?.title, desc: c.eligibilityItems?.[0]?.desc },
                  { icon: Users, num: "23", title: c.eligibilityItems?.[1]?.title, desc: c.eligibilityItems?.[1]?.desc },
                  { icon: ShieldCheck, num: "✔️", title: c.eligibilityItems?.[2]?.title, desc: c.eligibilityItems?.[2]?.desc }
                ].map((item, idx) => (
                  <motion.div key={idx} variants={fadeInUp} className="bg-muted/30 p-8 rounded-3xl border border-border/50 hover:shadow-md hover:-translate-y-1 transition-all duration-300 relative overflow-hidden group">
                    <div className={`absolute top-0 opacity-5 group-hover:opacity-10 transition-opacity flex items-center justify-center ${isRtl ? 'left-4' : 'right-4'} h-full`}>
                      <span className="text-9xl font-black font-display tracking-tighter">{item.num}</span>
                    </div>
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 text-primary relative z-10 shadow-sm">
                      <item.icon className="w-8 h-8" />
                    </div>
                    <h3 className="text-2xl font-bold font-display mb-4 relative z-10">{item.title}</h3>
                    <p className="text-muted-foreground font-body leading-relaxed text-[16px] relative z-10">{item.desc}</p>
                  </motion.div>
                ))}
              </motion.div>
            </section>

            <section className="bg-slate-950 text-white rounded-[2.5rem] p-8 md:p-16 overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/40 via-transparent to-blue-900/40"></div>
              <div className="relative z-10 text-center mb-16">
                <h2 className="text-3xl md:text-5xl font-display font-bold mb-4">{c.financeTitle}</h2>
              </div>

              <div className="relative z-10 grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                <motion.div className="bg-slate-900/80 backdrop-blur-md p-8 md:p-10 rounded-3xl border border-slate-700/50 flex flex-col" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                  <div className="w-16 h-16 bg-emerald-500/20 rounded-2xl flex items-center justify-center mb-6">
                    <Coins className="w-8 h-8 text-emerald-400" />
                  </div>
                  <h3 className="text-2xl font-bold font-display mb-4">{c.subventionsTitle}</h3>
                  <p className="text-slate-300 font-body mb-8 leading-relaxed text-lg">
                    {c.subventionsDesc}
                  </p>
                  <div className="mt-auto space-y-4">
                    {c.subventionsItems?.map((item, id) => (
                      <div key={id} className="bg-slate-800/60 p-4 rounded-2xl flex items-center gap-4 border border-slate-700/60">
                        <div className="w-10 h-10 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                           <TrendingUp className="w-5 h-5" />
                        </div>
                        <span className="font-body text-slate-200 font-semibold">{item}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                <motion.div className="bg-slate-900/80 backdrop-blur-md p-8 md:p-10 rounded-3xl border border-slate-700/50 flex flex-col" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} viewport={{ once: true }}>
                  <div className="w-16 h-16 bg-rose-500/20 rounded-2xl flex items-center justify-center mb-6">
                    <AlertTriangle className="w-8 h-8 text-rose-400" />
                  </div>
                  <h3 className="text-2xl font-bold font-display mb-4">{c.plafondsTitle}</h3>
                  <div className="bg-rose-500/10 border border-rose-500/20 rounded-2xl p-6 mb-6 text-center">
                    <span className="block text-sm text-rose-300/80 font-bold tracking-widest uppercase mb-1">{c.plafondsCard?.label || "Plafond Légal Max"}</span>
                    <span className="text-4xl sm:text-5xl font-black font-display text-rose-400 drop-shadow-md">{c.plafondsCard?.amount || "600 000"} <span className="text-2xl">{c.plafondsCard?.currency || "DHS"}</span></span>
                  </div>
                  <p className="text-slate-300 font-body mb-8 leading-relaxed">
                    {c.plafondsDesc}
                  </p>
                  <div className="mt-auto space-y-3">
                    {c.plafondsItems?.map((item, id) => {
                      const icons = [Landmark, FileSignature, AlertTriangle];
                      const Icon = icons[id % icons.length];
                      return (
                        <div key={id} className={`flex items-start gap-4 p-4 rounded-2xl bg-slate-800/50 border border-slate-700/50 ${id === 2 ? 'border-rose-500/30 bg-rose-500/5' : ''}`}>
                          <Icon className={`w-6 h-6 shrink-0 mt-0.5 ${id === 2 ? 'text-rose-400' : 'text-slate-400'}`} />
                          <span className="font-body text-slate-300 text-sm leading-snug">{item}</span>
                        </div>
                      )
                    })}
                  </div>
                </motion.div>
              </div>
            </section>

            <section id="timeline-section" className="bg-card border border-border/40 rounded-[2.5rem] p-8 md:p-16 shadow-sm">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-5xl font-display font-bold text-foreground">{c.timelineTitle}</h2>
              </div>

              <div className="max-w-4xl mx-auto relative px-0 sm:px-4">
                <div className={`absolute top-0 bottom-0 w-1 bg-border/60 transform md:-translate-x-1/2 ${isRtl ? 'right-8 md:right-1/2' : 'left-8 md:left-1/2'}`} />

                {[
                  { step: 1, title: c.timelineItems?.[0]?.title, desc: c.timelineItems?.[0]?.desc, icon: Users },
                  { step: 2, title: c.timelineItems?.[1]?.title, desc: c.timelineItems?.[1]?.desc, icon: BadgeCheck },
                  { step: 3, title: c.timelineItems?.[2]?.title, desc: c.timelineItems?.[2]?.desc, icon: MapPin },
                  { step: 4, title: c.timelineItems?.[3]?.title, desc: c.timelineItems?.[3]?.desc, icon: Milestone }
                ].map((item, idx) => {
                  const isEven = idx % 2 === 0;
                  let rowClass = "md:flex-row";
                  if (isEven) {
                    rowClass = "md:flex-row-reverse";
                  }
                  
                  const mlMobile = !isRtl ? "ml-[5rem] md:ml-0" : "";
                  const mrMobile = isRtl ? "mr-[5rem] md:mr-0" : "";

                  return (
                    <motion.div key={item.step} className={`relative flex items-center justify-between w-full mb-12 last:mb-0 ${rowClass}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                      <div className="hidden md:block w-[calc(50%-3rem)]" />
                      
                      <div className={`absolute flex items-center justify-center w-12 h-12 rounded-full bg-primary text-white border-4 border-card shadow-lg z-10 font-black text-xl transform ${isRtl ? 'translate-x-1/2 right-8 md:right-1/2' : '-translate-x-1/2 left-8 md:left-1/2'}`}>
                        {item.step}
                      </div>

                      <div className={`w-[calc(100%-5rem)] md:w-[calc(50%-3rem)] bg-muted/40 p-6 md:p-8 rounded-3xl border border-border/60 shadow-sm hover:shadow-md transition-all hover:-translate-y-1 ${mlMobile} ${mrMobile}`}>
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-3 bg-primary/10 rounded-2xl text-primary shrink-0">
                            <item.icon className="w-6 h-6" />
                          </div>
                          <h4 className="text-xl md:text-2xl font-bold font-display leading-tight">{item.title}</h4>
                        </div>
                        <p className="text-muted-foreground font-body text-[15px] leading-relaxed">{item.desc}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </section>
          </TabsContent>

          <TabsContent value="youth" className="mt-0 outline-none">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="bg-gradient-to-br from-indigo-50/50 to-purple-50/50 dark:from-slate-900 border border-indigo-100 dark:border-slate-800 p-8 md:p-12 rounded-[2.5rem] shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-accent/10 to-transparent rounded-full blur-[100px] -z-10 pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-indigo-500/10 to-transparent rounded-full blur-[80px] -z-10 pointer-events-none" />
                
                <div className="flex justify-center mb-10">
                  <div className="relative group">
                    <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-accent rounded-3xl blur opacity-25 group-hover:opacity-40 transition duration-1000 group-hover:duration-200" />
                    <img src="/assets/youth_candidate.png" alt="Jeunes candidats marocains" className="relative w-full max-w-3xl rounded-3xl shadow-xl object-cover border border-white/50 dark:border-slate-800" />
                  </div>
                </div>

                <div className="text-center mb-12">
                  <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 font-bold mb-6 border border-red-200 dark:border-red-500/30 shadow-sm">
                    <AlertTriangle className="w-5 h-5" />
                    {c.youth?.badge}
                  </div>
                  <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-display font-bold mb-6 text-indigo-950 dark:text-indigo-100 whitespace-nowrap overflow-hidden text-ellipsis">{c.youth?.title}</h2>
                  <p className="text-lg md:text-xl text-indigo-900/70 dark:text-indigo-200/80 font-body max-w-4xl mx-auto leading-relaxed">
                    {c.youth?.desc}
                  </p>
                </div>

                <div className="max-w-5xl mx-auto">
                  <div className="grid md:grid-cols-2 gap-8 mb-12">
                    <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm border border-slate-100 dark:border-slate-800 p-8 rounded-3xl shadow-sm hover:shadow-md transition-shadow h-full">
                      <h3 className="text-2xl font-display font-bold mb-8 text-slate-900 dark:text-slate-100 leading-tight">{c.youth?.eligibilityTitle}</h3>
                      <ul className="space-y-6 font-body">
                        {c.youth?.eligibilityItems?.map((item: string, i: number) => (
                          <li key={i} className="flex gap-4 items-start text-slate-600 dark:text-slate-300 text-[15px] leading-relaxed">
                            <CheckCircle className="w-6 h-6 text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm border border-slate-100 dark:border-slate-800 p-8 rounded-3xl shadow-sm hover:shadow-md transition-shadow h-full flex flex-col gap-6">
                      <h3 className="text-2xl font-display font-bold mb-4 text-slate-900 dark:text-slate-100 leading-tight">{c.youth?.financeTitle}</h3>
                      
                      <div className="flex flex-col sm:flex-row gap-6 bg-orange-50/40 dark:bg-orange-900/10 rounded-3xl p-6 border border-orange-100/60 dark:border-orange-800/20 items-center flex-1 shadow-sm">
                         <div className="relative w-28 h-28 shrink-0">
                           <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                             <circle cx="50" cy="50" r="38" stroke="currentColor" strokeWidth="14" fill="none" className="text-slate-200 dark:text-slate-700" />
                             <circle cx="50" cy="50" r="38" stroke="currentColor" strokeWidth="14" fill="none" strokeDasharray="238.7" strokeDashoffset="59.6" className="text-amber-500" strokeLinecap="round" />
                           </svg>
                           <div className="absolute inset-0 flex items-center justify-center flex-col">
                             <span className="text-2xl font-black text-slate-900 dark:text-white font-display tracking-tight">{c.youth?.percentAmount || "75%"}</span>
                           </div>
                         </div>
                         <div>
                           <h4 className="font-bold text-[17px] mb-2 text-slate-900 dark:text-slate-100 flex items-center gap-2">
                             <TrendingUp className="w-4 h-4 text-amber-500"/> {c.youth?.subventionsTitle}
                           </h4>
                           <p className="text-slate-600 dark:text-slate-400 font-body text-[14px] leading-relaxed">
                             {c.youth?.subventionsDesc}
                           </p>
                          </div>
                      </div>

                      {c.youth?.bonusTitle && (
                        <div className="flex bg-emerald-50/50 dark:bg-emerald-900/10 rounded-3xl p-6 border border-emerald-100/60 dark:border-emerald-800/20 items-center gap-5 shadow-sm">
                          <div className="w-16 h-16 rounded-2xl bg-emerald-100/60 dark:bg-emerald-800/30 flex flex-col items-center justify-center shrink-0">
                             <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-widest leading-none mb-0.5">+</span>
                             <span className="text-lg font-black text-emerald-800 dark:text-emerald-300 font-display leading-none">{c.youth?.bonusAmount || "10K"}</span>
                          </div>
                          <div>
                             <h4 className="font-bold text-[16px] mb-1.5 text-emerald-900 dark:text-emerald-100 flex items-center gap-2">
                               <Users className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
                               {c.youth.bonusTitle}
                             </h4>
                             <p className="text-emerald-700/80 dark:text-emerald-300/80 font-body text-[14px] leading-relaxed">
                               {c.youth.bonusDesc}
                             </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="pt-8">
                    <h4 className="font-bold text-2xl md:text-3xl text-center mb-10 text-slate-800 dark:text-slate-100 font-display">
                      {c.youth?.rulesTitle}
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {c.youth?.rulesItems?.map((item: string, i: number) => {
                        const icons = [TrendingUp, Calculator, Landmark, FileSignature];
                        const Icon = icons[i % icons.length];
                        const colorStyles = [
                          "bg-indigo-50/70 border-indigo-200/60 dark:bg-indigo-900/10 dark:border-indigo-800/20 text-indigo-900 dark:text-indigo-100",
                          "bg-orange-50/70 border-orange-200/60 dark:bg-orange-900/10 dark:border-orange-800/20 text-orange-900 dark:text-orange-100",
                          "bg-emerald-50/70 border-emerald-200/60 dark:bg-emerald-900/10 dark:border-emerald-800/20 text-emerald-900 dark:text-emerald-100",
                          "bg-rose-50/70 border-rose-200/60 dark:bg-rose-900/10 dark:border-rose-800/20 text-rose-900 dark:text-rose-100"
                        ];
                        const iconBgStyles = [
                          "bg-indigo-100/70 text-indigo-700 dark:bg-indigo-800/40 dark:text-indigo-400",
                          "bg-orange-100/70 text-orange-700 dark:bg-orange-800/40 dark:text-orange-400",
                          "bg-emerald-100/70 text-emerald-700 dark:bg-emerald-800/40 dark:text-emerald-400",
                          "bg-rose-100/70 text-rose-700 dark:bg-rose-800/40 dark:text-rose-400"
                        ];
                        
                        return (
                          <div key={i} className={`p-8 rounded-3xl border shadow-sm flex flex-col items-center text-center gap-5 hover:shadow-md transition-shadow ${colorStyles[i % 4]}`}>
                            <div className={`w-24 h-10 rounded-[100%] flex items-center justify-center ${iconBgStyles[i % 4]}`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <span className="text-[15px] font-body font-medium leading-relaxed max-w-sm">{item}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>
            );

            case "CandidacyRestrictionsSection":
              return (
                <section key={section.id} className="py-20 bg-muted/20 border-t border-border/40">
        <div className="container text-center max-w-4xl mx-auto">
          <h2 className="text-3xl font-display font-bold mb-6">{c.incompTitle}</h2>
          <p className="text-muted-foreground font-body text-lg mb-10">{c.incompDesc}</p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {c.incompItems?.map((item, id) => {
              const icons = ["⚖️", "👩‍⚖️", "📈", "👮‍♂️", "👔", "🏛️"];
              return (
                <div key={id} className="bg-card px-5 py-4 rounded-2xl border border-border/60 shadow-sm text-foreground flex items-center gap-3 font-semibold text-left hover:border-primary/30 transition-colors">
                  <span className="text-xl">{icons[id % icons.length]}</span> {item}
                </div>
              );
            })}
          </div>
          <p className="text-sm text-muted-foreground font-body italic flex items-center justify-center gap-2 bg-card/50 py-3 px-4 rounded-xl border border-border/40 inline-flex mb-12">
            <AlertTriangle className="w-4 h-4 text-orange-400" /> {c.incompNote}
          </p>

          {c.youth?.womensFundTitle && (
            <div className="mt-8 flex justify-center">
              <a 
                href={lang === 'ar' ? "https://nissaemoucharaka.ma/ar" : "https://nissaemoucharaka.ma/fr"} 
                target="_blank" 
                rel="noopener noreferrer"
                className="group flex flex-col items-center max-w-2xl bg-card border border-border/60 hover:border-primary/50 shadow-sm hover:shadow-md transition-all rounded-3xl p-8 text-center no-underline"
              >
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <Users className="w-8 h-8 text-primary" />
                </div>
                <h4 className="text-2xl font-display font-bold text-foreground mb-3">{c.youth.womensFundTitle}</h4>
                <p className="text-muted-foreground font-body text-[16px] leading-relaxed mb-2">
                  {c.youth.womensFundDesc}
                </p>
                <Button variant="outline" className="rounded-full px-8 py-6 font-bold uppercase tracking-wider text-sm">
                  {lang === 'ar' ? "زيارة المنصة" : (lang === 'zgh' ? "ⴽⵛⵎ ⵙ ⵓⵙⵉⵜ" : "Visiter la plateforme")} <ArrowRight className={`w-4 h-4 ${isRtl ? 'mr-2 rotate-180' : 'ml-2'}`} />
                </Button>
              </a>
            </div>
          )}
        </div>
      </section>
            );

            case "CandidacyCTASection":
              return (
                <section key={section.id} className="py-24 bg-gradient-to-br from-primary to-slate-900 text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1577568582294-8ba61fdeaf3c?q=80&w=2070&auto=format&fit=crop')] bg-cover bg-center mix-blend-overlay opacity-10" />
        
        <div className="container max-w-3xl relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-4xl font-display font-bold mb-6">{c.ctaTitle}</h2>
            <p className="text-xl mb-10 opacity-90 font-body">{c.ctaDesc}</p>
            <div className="flex flex-col items-center gap-6">
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button size="lg" variant="secondary" className="text-lg px-8 py-6 font-bold text-slate-900 shadow-md hover:bg-white rounded-xl" asChild>
                  <Link to="/engagement">{c.btnParti}</Link>
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 py-6 font-bold border-white/40 text-white bg-white/10 hover:bg-white/20 shadow-md backdrop-blur-sm rounded-xl" asChild>
                  <Link to="/agenda-national">{c.btnAgenda}</Link>
                </Button>
              </div>
              <Link to="/" className="text-white/70 hover:text-white underline underline-offset-4 font-body transition-colors mt-4 inline-flex items-center gap-2">
                <ArrowRight className={`w-4 h-4 ${isRtl ? 'rotate-180' : ''}`} /> {c.btnHome}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
            );

            case "CustomRichSection": {
              const langHtml = lang === "fr" ? section.content.html_fr : lang === "ar" ? section.content.html_ar : section.content.html_fr;
              return (
                <div key={section.id} className={`${section.content.bg_color || 'bg-background'} ${section.content.padding_y || 'py-12'}`}>
                  <div className="container mx-auto px-4">
                    <div dangerouslySetInnerHTML={{ __html: langHtml || '' }} />
                  </div>
                </div>
              );
            }

            default:
              return null;
          }
        })
      ) : (
        <div className="h-screen flex items-center justify-center">
          <span className="animate-pulse">Chargement de la page...</span>
        </div>
      )}

      <Footer />
    </div>
  );
}
